import type { TaskPlannerSchemaTypes } from "@bfc-software/api-schemas";
import { assertNever } from "@bfc-software/bfc-web-ui/lib/assert-never";

import type { Async } from "@bfc-software/bfc-web-ui/lib/async-utils";
import type { ActivatedTask } from "./types";
import type { AssignableSelector } from "./use-assignable-selectors";

type WireEligibleGroups =
  TaskPlannerSchemaTypes.components["schemas"]["TaskActivationSummary"]["eligible_user_groups"];

export type NonEmptyArray<T> = readonly [T, ...T[]];

/** Who may take a task, as the schema's three states (`TaskActivationSummary.eligible_user_groups`). */
export type TaskEligibility =
  /** Wire: key absent (or null). */
  | { kind: "anyone" }
  /** Wire: `[]`. */
  | { kind: "ungrouped-only" }
  /** Wire: non-empty. Selectors in one of these groups, or in none. */
  | { kind: "groups"; groupIds: NonEmptyArray<string> };

/** Wire -> domain: the one place absent / [] / non-empty become arms. */
export function eligibilityOf(wire: WireEligibleGroups): TaskEligibility {
  if (wire === undefined || wire === null) {
    return { kind: "anyone" };
  }
  const [first, ...rest] = wire.map((group) => group.group_id);
  if (first === undefined) {
    return { kind: "ungrouped-only" };
  }
  return { kind: "groups", groupIds: [first, ...rest] };
}

/** A selector's group, by whether orgcfg gave it an id. */
export type SelectorGroup =
  /** `id` is `String(appUserGroups.id)`, the wire's decimal `group_id` text. */
  | { kind: "identified"; id: string; name: string }
  /** orgcfg's `id?` absent: the selector is in a group, but it can match no wire id. */
  | { kind: "unidentified"; name: string };

/** The one number -> string conversion of orgcfg's group id. */
export function selectorGroupOf(group: {
  id?: number;
  name: string;
}): SelectorGroup {
  return group.id === undefined
    ? { kind: "unidentified", name: group.name }
    : { kind: "identified", id: String(group.id), name: group.name };
}

export function isEligible(
  eligibility: TaskEligibility,
  groups: ReadonlyArray<SelectorGroup>,
): boolean {
  switch (eligibility.kind) {
    case "anyone":
      return true;
    case "ungrouped-only":
      return groups.length === 0;
    case "groups":
      return (
        groups.length === 0 ||
        groups.some(
          (group) =>
            group.kind === "identified" &&
            eligibility.groupIds.includes(group.id),
        )
      );
    default:
      return assertNever(eligibility);
  }
}

/** Selectors eligible for EVERY task (intersection). No tasks: all selectors. */
export function eligibleSelectors(
  tasks: ReadonlyArray<ActivatedTask>,
  selectors: ReadonlyArray<AssignableSelector>,
): AssignableSelector[] {
  return selectors.filter((selector) =>
    tasks.every((task) => isEligible(task.eligibility, selector.groups)),
  );
}

/** What the dropdown has to say, as four cases (the empty one is NOT "loading"). */
export type AssignOptions =
  | { kind: "loading" }
  | { kind: "failed"; error: unknown }
  | { kind: "nobody-eligible" }
  | { kind: "eligible"; selectors: NonEmptyArray<AssignableSelector> };

export function assignOptionsOf(
  selectors: Async<ReadonlyArray<AssignableSelector>>,
  tasks: ReadonlyArray<ActivatedTask>,
): AssignOptions {
  switch (selectors.kind) {
    case "loading":
      return { kind: "loading" };
    case "failed":
      return { kind: "failed", error: selectors.error };
    case "loaded": {
      const [first, ...rest] = eligibleSelectors(tasks, selectors.value);
      return first === undefined
        ? { kind: "nobody-eligible" }
        : { kind: "eligible", selectors: [first, ...rest] };
    }
    default:
      return assertNever(selectors);
  }
}
